import * as React from 'react';
import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import Button from '../../core/components/Button.tsx';
import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import FormField from '../../core/components/FormField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import {
    faPlus,
    faTrash,
    faCode,
    faAlignLeft,
    faFlask,
    faChevronDown,
    faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../core/utils/classnames.tsx';
import { GET_DATA_IMPORT_CONFIG, UPDATE_DATA_IMPORT_CONFIG_RAW } from '../queries.ts';
import { getTransformerSchema, TransformerSchema } from '../transformerSchemas.ts';
import SelectField, { Option } from '../../core/components/Fields/SelectField.tsx';
import Alert, { AlertTitle } from '../../core/components/Alert.tsx';
import Table, { TableScrollWrapper } from '../../core/components/Table.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import DryRunInputSelector from './DryRunInputSelector.tsx';
import useDryRunDataImport from '../hooks/useDryRunDataImport.ts';
import useSftpPatternFiles from '../hooks/useSftpPatternFiles.ts';
import { DryRunInput, DryRunInputMode, DryRunResult, RawTransformerConfig } from '../types.ts';
import { patternsFromTransformerConfig } from '../files.ts';
import { DRY_RUN_MAX_UPLOAD_BYTES } from '../constants.ts';

const FILE_READER_KWARGS_KEYS = ['delimiter', 'encoding', 'quotechar', 'escapechar'] as const;
type FileReaderKwargsKey = (typeof FILE_READER_KWARGS_KEYS)[number];
type FileReaderKwargs = Record<FileReaderKwargsKey, string | null>;

const AddIcon = withIcon(faPlus);
const DeleteIcon = withIcon(faTrash);
const JsonIcon = withIcon(faCode);
const FormatIcon = withIcon(faAlignLeft);
const DryRunIcon = withIcon(faFlask);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronRightIcon = withIcon(faChevronRight);

const DRY_RUN_MAX_ROWS = 100;

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <div className="text-sm font-medium text-content-strong">{children}</div>
);

const AddRowButton = ({
    onClick,
    disabled,
    children,
}: {
    onClick: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}) => (
    <Button type="button" onClick={onClick} disabled={disabled} className="self-start flex gap-2 items-center">
        <AddIcon />
        {children}
    </Button>
);

const IconButton = ({
    onClick,
    className,
    title,
    children,
}: {
    onClick: () => void;
    className?: string;
    title?: string;
    children: React.ReactNode;
}) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className={classnames(
            'flex items-center justify-center w-7 h-7 rounded text-content-weak hover:bg-surface-strong hover:text-content-medium cursor-pointer transition-colors',
            className
        )}
    >
        {children}
    </button>
);

const Dropdown = ({
    value,
    onChange,
    options,
    className,
    disabled,
}: {
    value: string;
    onChange: (value: string) => void;
    options: Option[];
    className?: string;
    disabled?: boolean;
}) => {
    const allOptions =
        value && !options.some((o) => o.value === value) ? [{ value, label: value }, ...options] : options;
    return (
        <SelectField
            value={value}
            onValueChange={onChange}
            options={allOptions}
            renderOption={(option) => option.label}
            required
            className={className}
            disabled={disabled}
        />
    );
};

const ENCODING_OPTIONS: Option[] = [
    { value: 'utf-8', label: 'utf-8' },
    { value: 'utf-8-sig', label: 'utf-8-sig (with BOM)' },
    { value: 'utf-16', label: 'utf-16' },
    { value: 'latin-1', label: 'latin-1 (iso-8859-1)' },
    { value: 'cp1252', label: 'cp1252 (windows-1252)' },
    { value: 'ascii', label: 'ascii' },
];

const QUOTECHAR_OPTIONS: Option[] = [
    { value: '"', label: '" (double quote)' },
    { value: "'", label: "' (single quote)" },
];

const FileNamePatternsEditor = ({
    patterns,
    onChange,
    t,
}: {
    patterns: string[];
    onChange: (patterns: string[]) => void;
    t: (key: string) => string;
}) => (
    <div className="flex flex-col gap-2">
        <SectionLabel>{t('importConfig.form.fileNamePatterns')}</SectionLabel>

        <div className="flex flex-col gap-2">
            {patterns.map((pattern, index) => (
                <div key={index} className="flex gap-2 items-center">
                    <StringField
                        value={pattern}
                        onValueChange={(value) => {
                            const updated = [...patterns];
                            updated[index] = value;
                            onChange(updated);
                        }}
                        placeholder={t('importConfig.form.fileNamePatternPlaceholder')}
                        className="flex-1"
                    />
                    <IconButton
                        onClick={() => onChange(patterns.filter((_, i) => i !== index))}
                        title={t('importConfig.form.removePattern')}
                    >
                        <DeleteIcon className="text-content-danger-strong" />
                    </IconButton>
                </div>
            ))}

            <span className="text-content-medium text-xs">{t('importConfig.form.patternHelpText')}</span>

            <AddRowButton onClick={() => onChange([...patterns, ''])}>{t('importConfig.form.addPattern')}</AddRowButton>
        </div>
    </div>
);

const defaultExpressionFor = (columnName: string): string => (columnName === 'partition_id' ? "'main'" : '');

// the detected columns that are not a target column of the schema are file columns without a mapping
const isTargetColumn = (schema: TransformerSchema | null, columnName: string) =>
    !schema || schema.allowedColumns.includes(columnName);

const ColumnsEditor = ({
    columns,
    onChange,
    schema,
    t,
}: {
    columns: [string, string][];
    onChange: (columns: [string, string][]) => void;
    schema: TransformerSchema | null;
    t: (key: string) => string;
}) => {
    const mandatorySet = new Set(schema?.mandatoryColumns ?? []);
    const usedNames = new Set(columns.map(([key]) => key).filter(Boolean));
    // columns whose row has no expression can still be picked, see the dropdown's onChange
    const mappedNames = new Set(columns.filter(([, value]) => value).map(([key]) => key));

    const availableOptions = (currentKey: string) => {
        if (!schema) return [];
        return schema.allowedColumns.filter((name) => name === currentKey || !mappedNames.has(name));
    };

    const unusedAllowed = schema ? schema.allowedColumns.filter((name) => !usedNames.has(name)) : [];
    const canAddMore = !schema || unusedAllowed.length > 0;

    return (
        <div className="flex flex-col gap-2">
            <SectionLabel>{t('importConfig.form.columns')}</SectionLabel>
            <span className="text-content-medium text-xs mb-2">{t('importConfig.form.columnsHelpText')}</span>
            <div className="flex flex-col gap-2">
                {columns.length > 0 && (
                    <div className="flex gap-2 text-xs text-content-medium px-0.5">
                        <span className="flex-1">{t('importConfig.form.columnName')}</span>
                        <span className="flex-1">{t('importConfig.form.columnExpression')}</span>
                        <span className="w-7" />
                    </div>
                )}
                {columns.map(([key, value], index) => {
                    const isMandatory = mandatorySet.has(key);
                    const isUnknown = schema != null && key !== '' && !schema.allowedColumns.includes(key);
                    return (
                        <div key={index} className="flex gap-2 items-center">
                            {schema ? (
                                <div className="flex-1 flex items-center gap-1.5 min-w-0">
                                    <Dropdown
                                        value={key}
                                        onChange={(newKey) => {
                                            const updated = [...columns];
                                            // the picked column's row without an expression takes over this row's
                                            // expression, e.g. for a file column that was not mapped
                                            const emptyIndex = columns.findIndex(
                                                ([otherKey, otherValue], i) =>
                                                    i !== index && otherKey === newKey && !otherValue
                                            );
                                            if (newKey && emptyIndex !== -1) {
                                                updated[emptyIndex] = [newKey, value];
                                                onChange(updated.filter((_, i) => i !== index));
                                                return;
                                            }
                                            const nextValue = value === '' ? defaultExpressionFor(newKey) : value;
                                            updated[index] = [newKey, nextValue];
                                            onChange(updated);
                                        }}
                                        options={[
                                            // rows without a column name are not saved
                                            { value: '', label: t('importConfig.form.notMapped') },
                                            ...availableOptions(key).map((name) => ({
                                                value: name,
                                                label: mandatorySet.has(name) ? `${name} *` : name,
                                            })),
                                        ]}
                                        className="flex-1"
                                        disabled={isMandatory}
                                    />
                                    {isMandatory && (
                                        <span
                                            className="text-xs text-content-danger-strong shrink-0"
                                            title={t('importConfig.form.mandatoryColumn')}
                                        >
                                            *
                                        </span>
                                    )}
                                    {isUnknown && (
                                        <span
                                            className="text-xs text-content-warning-strong shrink-0"
                                            title={t('importConfig.form.unknownColumn')}
                                        >
                                            !
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <StringField
                                    value={key}
                                    onValueChange={(newKey) => {
                                        const updated = [...columns];
                                        updated[index] = [newKey, value];
                                        onChange(updated);
                                    }}
                                    placeholder={t('importConfig.form.columnNamePlaceholder')}
                                    className="flex-1"
                                />
                            )}
                            <StringField
                                value={value}
                                onValueChange={(newValue) => {
                                    const updated = [...columns];
                                    updated[index] = [key, newValue];
                                    onChange(updated);
                                }}
                                placeholder={t('importConfig.form.columnExpressionPlaceholder')}
                                className="flex-1"
                            />
                            <IconButton
                                onClick={() => onChange(columns.filter((_, i) => i !== index))}
                                title={
                                    isMandatory
                                        ? t('importConfig.form.cannotRemoveMandatory')
                                        : t('importConfig.form.removeColumn')
                                }
                                className={isMandatory ? 'opacity-40 pointer-events-none' : ''}
                            >
                                <DeleteIcon className="text-content-danger-strong" />
                            </IconButton>
                        </div>
                    );
                })}
                {schema && columns.some(([key]) => !key) && (
                    <span className="text-xs text-content-warning-strong">
                        {t('importConfig.form.notMappedRemovedOnSave')}
                    </span>
                )}
                <AddRowButton disabled={!canAddMore} onClick={() => onChange([...columns, ['', '']])}>
                    {t('importConfig.form.addColumn')}
                </AddRowButton>
            </div>
        </div>
    );
};

const FileReaderKwargsEditor = ({
    kwargs,
    onChange,
    t,
}: {
    kwargs: FileReaderKwargs;
    onChange: (kwargs: FileReaderKwargs) => void;
    t: (key: string) => string;
}) => {
    // a cleared field removes the parameter from the saved configuration entirely
    const handleValueChange = (key: FileReaderKwargsKey) => (value: string) => onChange({ ...kwargs, [key]: value });

    const withNotSetOption = (options: Option[]): Option[] => [
        { value: '', label: t('importConfig.form.kwargs.notSet') },
        ...options,
    ];

    return (
        <div className="flex flex-col gap-2">
            <SectionLabel>{t('importConfig.form.fileReaderKwargs')}</SectionLabel>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField label={t('importConfig.form.kwargs.delimiter')}>
                    <StringField
                        value={kwargs.delimiter ?? ''}
                        onValueChange={handleValueChange('delimiter')}
                        placeholder={t('importConfig.form.kwargs.delimiterPlaceholder')}
                        maxLength={1}
                    />
                </FormField>

                <FormField label={t('importConfig.form.kwargs.encoding')}>
                    <SelectField
                        value={kwargs.encoding ?? ''}
                        onValueChange={handleValueChange('encoding')}
                        options={withNotSetOption(ENCODING_OPTIONS)}
                        placeholder={t('importConfig.form.kwargs.notSet')}
                    />
                </FormField>

                <FormField label={t('importConfig.form.kwargs.quotechar')}>
                    <SelectField
                        value={kwargs.quotechar ?? ''}
                        onValueChange={handleValueChange('quotechar')}
                        options={withNotSetOption(QUOTECHAR_OPTIONS)}
                        placeholder={t('importConfig.form.kwargs.notSet')}
                    />
                </FormField>

                <FormField label={t('importConfig.form.kwargs.escapechar')}>
                    <StringField
                        value={kwargs.escapechar ?? ''}
                        onValueChange={handleValueChange('escapechar')}
                        placeholder={t('importConfig.form.kwargs.notSet')}
                        maxLength={1}
                    />
                </FormField>
            </div>
        </div>
    );
};

const JsonEditor = ({
    rawJson,
    onRawJsonChange,
    jsonError,
    onFormat,
    t,
}: {
    rawJson: string;
    onRawJsonChange: (value: string) => void;
    jsonError: string | null;
    onFormat: () => void;
    t: (key: string) => string;
}) => (
    <div className="flex flex-col gap-2 flex-1">
        <div className="flex items-center justify-between">
            <SectionLabel>{t('importConfig.json.title')}</SectionLabel>
            <Button variant="ghost" onClick={onFormat} className="flex gap-2 items-center">
                <FormatIcon />
                {t('importConfig.json.format')}
            </Button>
        </div>
        <textarea
            value={rawJson}
            onChange={(e) => onRawJsonChange(e.target.value)}
            className={classnames(
                'flex-1 min-h-[24rem] px-3 py-2 bg-surface-weak text-sm text-content-strong border border-solid rounded outline-none font-mono resize-none',
                'hover:border-line-hover focus:border-line-focus transition-colors',
                jsonError ? 'border-line-danger-strong' : 'border-line-default'
            )}
            spellCheck={false}
        />
        {jsonError && <div className="text-xs text-content-danger-strong">{jsonError}</div>}
    </div>
);

const CollapsibleSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="flex flex-col gap-2">
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1.5 self-start text-sm font-semibold text-content-strong cursor-pointer"
            >
                {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                {title}
            </button>
            {expanded && children}
        </div>
    );
};

const SampleRowsTable = ({ rows }: { rows: Record<string, unknown>[] }) => {
    const columns = Object.keys(rows[0] ?? {});

    return (
        <TableScrollWrapper>
            <Table className="w-full table-auto">
                <Table.Head>
                    <Table.Row>
                        {columns.map((column) => (
                            <Table.HeadCell key={column}>{column}</Table.HeadCell>
                        ))}
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {rows.map((row, index) => (
                        <Table.Row key={index}>
                            {columns.map((column) => (
                                <Table.Cell key={column} className="whitespace-nowrap">
                                    {row[column] == null ? '' : String(row[column])}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </TableScrollWrapper>
    );
};

const DetectedConfigAlert = ({
    transformerConfig,
    detectedTransformerConfig,
    schema,
    onApply,
}: {
    transformerConfig: RawTransformerConfig;
    detectedTransformerConfig: RawTransformerConfig;
    schema: TransformerSchema | null;
    onApply: (detectedTransformerConfig: RawTransformerConfig) => void;
}) => {
    const { t } = useTranslation('masterdata');

    const detectedColumns = Object.entries(detectedTransformerConfig.columns ?? {});
    // A detected column is empty when the file has no column of that name, which is no reason to clear
    // the form's mapping
    const changes = [
        ...FILE_READER_KWARGS_KEYS.map((key) => ({
            label: t(`importConfig.form.kwargs.${key}`),
            current: transformerConfig.file_reader_kwargs?.[key] ?? '',
            detected: detectedTransformerConfig.file_reader_kwargs?.[key] ?? '',
        })),
        ...detectedColumns
            .filter(([column]) => isTargetColumn(schema, column))
            .map(([column, expression]) => ({
                label: column,
                current: transformerConfig.columns?.[column] ?? '',
                detected: expression,
            })),
    ].filter(({ current, detected }) => detected && detected !== current);
    const mappedFileColumns = new Set(Object.values(transformerConfig.columns ?? {}));
    const notMappedFileColumns = detectedColumns
        .filter(([column, fileColumn]) => !isTargetColumn(schema, column) && !mappedFileColumns.has(fileColumn))
        .map(([, fileColumn]) => fileColumn);

    if (changes.length === 0 && notMappedFileColumns.length === 0) {
        return null;
    }
    return (
        <Alert severity="info">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <span className="font-medium">{t('importConfig.dryRun.detectedConfig.title')}</span>
                    <span className="text-xs text-content-medium">
                        {t('importConfig.dryRun.detectedConfig.description')}
                    </span>
                </div>
                {changes.length > 0 && (
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                        {changes.map(({ label, current, detected }) => (
                            <React.Fragment key={label}>
                                <span className="text-content-medium">{label}</span>
                                <span className="break-all">
                                    {current ? (
                                        <span className="font-mono">{current.replace('\t', '\\t')}</span>
                                    ) : (
                                        t('importConfig.form.kwargs.notSet')
                                    )}
                                    {' → '}
                                    <span className="font-mono">{detected.replace('\t', '\\t')}</span>
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                )}
                {notMappedFileColumns.length > 0 && (
                    <span className="break-all">
                        {t('importConfig.dryRun.detectedConfig.notMappedFileColumns', {
                            columns: notMappedFileColumns.join(', '),
                        })}
                    </span>
                )}
                <Button type="button" onClick={() => onApply(detectedTransformerConfig)} className="self-start">
                    {t('importConfig.dryRun.detectedConfig.apply')}
                </Button>
            </div>
        </Alert>
    );
};

const DryRunPanel = ({
    result,
    requestError,
    loading,
    transformerConfig,
    schema,
    onApplyDetectedConfig,
    t,
}: {
    result: DryRunResult | null;
    requestError: string | null;
    loading: boolean;
    transformerConfig: RawTransformerConfig | null;
    schema: TransformerSchema | null;
    onApplyDetectedConfig: (detectedTransformerConfig: RawTransformerConfig) => void;
    t: (key: string, options?: Record<string, unknown>) => string;
}) => {
    if (!loading && !requestError && !result) {
        return null;
    }
    return (
        <div className="flex flex-col gap-4">
            {loading ? (
                <div className="text-sm text-content-medium">{t('importConfig.dryRun.running')}</div>
            ) : requestError ? (
                <Alert severity="error">{requestError}</Alert>
            ) : result ? (
                <>
                    {result.success ? (
                        <Alert severity="success">
                            {t('importConfig.dryRun.successMessage', { count: result.transformedRowsCount ?? 0 })}
                        </Alert>
                    ) : (
                        <Alert severity="error">
                            <AlertTitle>{t('importConfig.dryRun.failureMessage')}</AlertTitle>
                            {result.error && (
                                <div className="font-mono text-xs whitespace-pre-wrap">{result.error}</div>
                            )}
                        </Alert>
                    )}

                    {transformerConfig && result.detectedTransformerConfig && (
                        <DetectedConfigAlert
                            transformerConfig={transformerConfig}
                            detectedTransformerConfig={result.detectedTransformerConfig}
                            schema={schema}
                            onApply={onApplyDetectedConfig}
                        />
                    )}

                    {result.success && result.sampleRows && result.sampleRows.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <SectionLabel>{t('importConfig.dryRun.sampleRowsTitle')}</SectionLabel>
                            <SampleRowsTable rows={result.sampleRows} />
                        </div>
                    )}

                    {result.logs && (
                        <CollapsibleSection title={t('importConfig.dryRun.logsTitle')}>
                            <pre className="max-h-[20rem] overflow-auto px-3 py-2 bg-surface-weak text-xs text-content-strong border border-solid border-line-default rounded font-mono whitespace-pre-wrap">
                                {result.logs}
                            </pre>
                        </CollapsibleSection>
                    )}

                    {!result.success && result.errorTraceback && (
                        <div className="flex flex-col gap-2">
                            <SectionLabel>{t('importConfig.dryRun.tracebackTitle')}</SectionLabel>
                            <pre className="max-h-[20rem] overflow-auto px-3 py-2 bg-surface-weak text-xs text-content-danger-strong border border-solid border-line-default rounded font-mono whitespace-pre-wrap">
                                {result.errorTraceback}
                            </pre>
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};

interface TransformerConfigFormState {
    fileNamePatterns: string[];
    columns: [string, string][];
    fileReaderKwargs: FileReaderKwargs;
}

const buildTransformerConfig = (
    savedRawConfig: string | undefined,
    { fileNamePatterns, columns, fileReaderKwargs }: TransformerConfigFormState
) => {
    const columnsObj: Record<string, string> = {};
    for (const [key, value] of columns) {
        // rows without an expression are treated as "no mapping" and are not saved
        if (key.trim() && value.trim()) columnsObj[key] = value;
    }

    // the raw mutation replaces the whole config: merging into the saved one keeps keys the form
    // does not manage (e.g. transformer_module), and cleared fields are actually removed (the
    // partial-update mutation would keep the previously saved values)
    let fullConfig: Record<string, unknown>;
    try {
        fullConfig = savedRawConfig ? JSON.parse(savedRawConfig) : {};
    } catch {
        fullConfig = {};
    }
    const kwargsObj = { ...((fullConfig.file_reader_kwargs as Record<string, unknown>) || {}) };
    for (const key of FILE_READER_KWARGS_KEYS) {
        if (fileReaderKwargs[key]) {
            kwargsObj[key] = fileReaderKwargs[key];
        } else {
            delete kwargsObj[key];
        }
    }
    // the delimiter field also manages the legacy `sep` alias; keeping both would
    // pass conflicting kwargs to the file reader
    delete kwargsObj.sep;

    fullConfig.file_name_patterns = fileNamePatterns.filter((p) => p.trim());
    delete fullConfig.file_name_pattern; // legacy singular alias, shadows the plural key

    if (Object.keys(columnsObj).length > 0) {
        fullConfig.columns = columnsObj;
    } else {
        delete fullConfig.columns;
    }
    if (Object.keys(kwargsObj).length > 0) {
        fullConfig.file_reader_kwargs = kwargsObj;
    } else {
        delete fullConfig.file_reader_kwargs;
    }

    return fullConfig;
};

interface LoadedTransformerConfig {
    fileNamePatterns?: string[];
    columns?: Record<string, string>;
    fileReaderKwargs?: Record<string, string | null>;
    rawConfig?: string;
}

const SaveBar = ({
    hasUnsavedChanges,
    onDiscard,
    onSave,
    canSave,
    isSaving,
    t,
}: {
    hasUnsavedChanges: boolean;
    onDiscard: () => void;
    onSave: () => void;
    canSave: boolean;
    isSaving: boolean;
    t: (key: string) => string;
}) => (
    <div
        className={classnames(
            'flex items-center gap-4 px-4 py-3 rounded-lg border border-solid border-line-weak bg-surface-weak',
            hasUnsavedChanges && 'sticky bottom-4 z-10 shadow-sm'
        )}
    >
        <span
            className={classnames(
                'w-2.5 h-2.5 flex-none rounded-full',
                hasUnsavedChanges ? 'bg-surface-warning-strong' : 'bg-line-strong'
            )}
        />
        <div className="flex flex-col flex-1 min-w-0">
            <span className="text-sm font-medium text-content-strong">
                {hasUnsavedChanges ? t('importConfig.saveBar.unsavedTitle') : t('importConfig.saveBar.savedTitle')}
            </span>
            <span className="text-xs text-content-medium">
                {hasUnsavedChanges
                    ? t('importConfig.saveBar.unsavedDescription')
                    : t('importConfig.saveBar.savedDescription')}
            </span>
        </div>
        <div className="flex gap-2">
            <Button type="button" onClick={onDiscard} disabled={!hasUnsavedChanges || isSaving}>
                {t('importConfig.saveBar.discard')}
            </Button>
            <Button
                type="button"
                variant="primary"
                onClick={onSave}
                disabled={!hasUnsavedChanges || !canSave}
                loading={isSaving}
            >
                {t('importConfig.save')}
            </Button>
        </div>
    </div>
);

interface DataImportConfigViewProps {
    lookupDefinitionId: string;
    lookupType?: string | null;
    showRawJsonToggle?: boolean;
}

// Renders the import configuration and dry run sections of the master data detail page
export const DataImportConfigView = ({
    lookupDefinitionId,
    lookupType,
    showRawJsonToggle = true,
}: DataImportConfigViewProps) => {
    const { t } = useTranslation('masterdata');
    const { publishToast } = useToaster();
    const [isRawJsonView, setRawJsonView] = useState(false);
    const schema = getTransformerSchema(lookupType);

    // Form state
    const [fileNamePatterns, setFileNamePatterns] = useState<string[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [columns, setColumns] = useState<[string, string][]>([]);
    const [fileReaderKwargs, setFileReaderKwargs] = useState<FileReaderKwargs>({
        delimiter: null,
        encoding: null,
        quotechar: null,
        escapechar: null,
    });

    // JSON state
    const [rawJson, setRawJson] = useState('');
    const [jsonError, setJsonError] = useState<string | null>(null);

    const [hasFormChanges, setHasFormChanges] = useState(false);
    const [hasJsonChanges, setHasJsonChanges] = useState(false);

    const [dryRunInputMode, setDryRunInputMode] = useState<DryRunInputMode>('PATTERNS');
    const [uploadFileByPattern, setUploadFileByPattern] = useState<Record<string, File | undefined>>({});

    const loadedConfig = useRef<LoadedTransformerConfig>({});

    const populateFromConfig = useCallback(
        (config: LoadedTransformerConfig) => {
            loadedConfig.current = config;
            setHasFormChanges(false);
            setHasJsonChanges(false);
            setFileNamePatterns(config.fileNamePatterns || []);
            const existing: [string, string][] = Object.entries(config.columns || {});
            let nextColumns: [string, string][];
            if (schema) {
                // always show the mandatory columns, even for a fresh configuration
                const existingKeys = new Set(existing.map(([k]) => k));
                const missingMandatory: [string, string][] = schema.mandatoryColumns
                    .filter((name) => !existingKeys.has(name))
                    .map((name) => [name, defaultExpressionFor(name)]);
                nextColumns = [...missingMandatory, ...existing];
            } else {
                nextColumns = existing;
            }
            setColumns(nextColumns);
            const kwargsObj = config.fileReaderKwargs || {};
            setFileReaderKwargs({
                delimiter: kwargsObj.delimiter != null ? String(kwargsObj.delimiter) : null,
                encoding: kwargsObj.encoding != null ? String(kwargsObj.encoding) : null,
                quotechar: kwargsObj.quotechar != null ? String(kwargsObj.quotechar) : null,
                escapechar: kwargsObj.escapechar != null ? String(kwargsObj.escapechar) : null,
            });

            const hasExistingConfig =
                (config.fileNamePatterns && config.fileNamePatterns.length > 0) ||
                Object.keys(config.columns || {}).length > 0 ||
                Object.keys(kwargsObj).some((k) => (kwargsObj as Record<string, unknown>)[k] != null);

            if (config.rawConfig && hasExistingConfig) {
                setRawJson(formatJson(config.rawConfig));
            } else {
                const starter: Record<string, unknown> = {
                    file_name_patterns: config.fileNamePatterns || [],
                };
                if (nextColumns.length > 0) {
                    starter.columns = Object.fromEntries(nextColumns);
                }
                setRawJson(JSON.stringify(starter, null, 2));
            }
        },
        [schema]
    );

    const { loading } = useQuery(GET_DATA_IMPORT_CONFIG, {
        variables: { lookupDefinitionId },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const config = data?.dataImportConfig?.transformerConfig;
            populateFromConfig(config || {});
            setFileNames(data?.dataImportConfig?.fileNames || []);
        },
    });

    const {
        runDryRun,
        resetDryRun,
        result: dryRunResult,
        requestError: dryRunRequestError,
        isRunning: isDryRunRunning,
    } = useDryRunDataImport();

    const [updateConfigRaw, { loading: isSaving }] = useMutation(UPDATE_DATA_IMPORT_CONFIG_RAW, {
        onCompleted: (data) => {
            const config = data?.updateDataImportConfigRaw?.dataImportConfig?.transformerConfig;
            if (config) {
                populateFromConfig(config);
            }
            publishToast({ description: t('importConfig.saveSuccess'), status: 'success' });
        },
        onError: (error) => {
            console.error(error);
            publishToast({ description: t('importConfig.saveError'), status: 'error' });
        },
    });

    const handleFormChange = useCallback(
        <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
            (value: T) => {
                setter(value);
                setHasFormChanges(true);
            },
        []
    );

    const handleRawJsonChange = useCallback((value: string) => {
        setRawJson(value);
        setHasJsonChanges(true);
        try {
            JSON.parse(value);
            setJsonError(null);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }, []);

    const handleFormatJson = useCallback(() => {
        try {
            setRawJson(formatJson(rawJson));
            setJsonError(null);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }, [rawJson]);

    const handleSaveForm = useCallback(() => {
        const fullConfig = buildTransformerConfig(loadedConfig.current.rawConfig, {
            fileNamePatterns,
            columns,
            fileReaderKwargs,
        });
        updateConfigRaw({
            variables: {
                lookupDefinitionId,
                rawTransformerConfig: JSON.stringify(fullConfig),
            },
        });
    }, [lookupDefinitionId, fileNamePatterns, columns, fileReaderKwargs, updateConfigRaw]);

    const handleSaveJson = useCallback(() => {
        if (jsonError) return;
        updateConfigRaw({
            variables: {
                lookupDefinitionId,
                rawTransformerConfig: rawJson,
            },
        });
    }, [lookupDefinitionId, rawJson, jsonError, updateConfigRaw]);

    const handleDiscard = useCallback(() => {
        populateFromConfig(loadedConfig.current);
        setJsonError(null);
    }, [populateFromConfig]);

    // memoized, so the debounced SFTP preview only refetches when the patterns change
    const currentPatterns = useMemo(
        () =>
            isRawJsonView
                ? patternsFromTransformerConfig(rawJson)
                : fileNamePatterns.filter((pattern) => pattern.trim()),
        [isRawJsonView, rawJson, fileNamePatterns]
    );

    const sftpPatternFiles = useSftpPatternFiles(lookupDefinitionId, currentPatterns, dryRunInputMode !== 'SFTP');
    const sftpFiles = sftpPatternFiles.patternFiles.flatMap(({ pattern, file }) =>
        file ? [{ path: file.path, fileNamePattern: pattern }] : []
    );
    // files of patterns removed in the meantime are left out
    const uploadFiles = currentPatterns.flatMap((pattern) => {
        const file = uploadFileByPattern[pattern];
        return file ? [{ file, fileNamePattern: pattern }] : [];
    });

    const handleDryRun = useCallback(() => {
        const transformerConfig = isRawJsonView
            ? rawJson
            : JSON.stringify(
                  buildTransformerConfig(loadedConfig.current.rawConfig, {
                      fileNamePatterns,
                      columns,
                      fileReaderKwargs,
                  })
              );
        const inputByMode: Record<DryRunInputMode, DryRunInput> = {
            PATTERNS: { mode: 'PATTERNS' },
            SFTP: { mode: 'SFTP', sftpFiles },
            UPLOAD: { mode: 'UPLOAD', files: uploadFiles },
        };
        runDryRun({
            lookupDefinitionId,
            maxRows: DRY_RUN_MAX_ROWS,
            transformerConfig,
            input: inputByMode[dryRunInputMode],
        });
    }, [
        isRawJsonView,
        rawJson,
        fileNamePatterns,
        columns,
        fileReaderKwargs,
        sftpFiles,
        uploadFiles,
        dryRunInputMode,
        lookupDefinitionId,
        runDryRun,
    ]);

    // the raw JSON editor is not synced with the form, so the detected config is only offered in the form
    const formTransformerConfig = isRawJsonView
        ? null
        : buildTransformerConfig(loadedConfig.current.rawConfig, { fileNamePatterns, columns, fileReaderKwargs });

    const handleApplyDetectedConfig = (detectedTransformerConfig: RawTransformerConfig) => {
        const kwargs = { ...fileReaderKwargs };
        for (const key of FILE_READER_KWARGS_KEYS) {
            kwargs[key] = detectedTransformerConfig.file_reader_kwargs?.[key] || kwargs[key];
        }
        const detectedColumns = detectedTransformerConfig.columns ?? {};
        const existingKeys = new Set(columns.map(([key]) => key));
        const expressions = new Set(columns.map(([, value]) => value));
        setFileReaderKwargs(kwargs);
        setColumns([
            ...columns.map(([key, value]): [string, string] => [key, detectedColumns[key] || value]),
            ...Object.entries(detectedColumns).filter(
                ([key, value]) =>
                    isTargetColumn(schema, key) &&
                    !existingKeys.has(key) &&
                    (value || schema?.mandatoryColumns.includes(key))
            ),
            // file columns without a target column are added as not mapped, unless a row already uses them
            ...Object.entries(detectedColumns)
                .filter(
                    ([key, fileColumn]) => fileColumn && !isTargetColumn(schema, key) && !expressions.has(fileColumn)
                )
                .map(([, fileColumn]): [string, string] => ['', fileColumn]),
        ]);
        setHasFormChanges(true);
    };

    const hasUnsavedChanges = isRawJsonView ? hasJsonChanges : hasFormChanges;
    const hasJsonError = isRawJsonView && !!jsonError;

    const isUploadTooLarge = uploadFiles.reduce((sum, { file }) => sum + file.size, 0) > DRY_RUN_MAX_UPLOAD_BYTES;
    const hasDryRunInput = {
        PATTERNS: currentPatterns.length > 0,
        SFTP: sftpFiles.length > 0 && !sftpPatternFiles.loading,
        UPLOAD: uploadFiles.length > 0 && !isUploadTooLarge,
    }[dryRunInputMode];

    return (
        <div className="flex flex-col gap-6 max-w-[48rem]">
            <SettingsCard
                title={t('hub.detail.config.title')}
                description={t('hub.detail.config.description')}
                controls={
                    showRawJsonToggle && (
                        <Button
                            type="button"
                            active={isRawJsonView}
                            onClick={() => setRawJsonView(!isRawJsonView)}
                            className="flex gap-2 items-center"
                        >
                            <JsonIcon />
                            {t('importConfig.tabs.json')}
                        </Button>
                    )
                }
            >
                {loading ? (
                    <div className="flex items-center justify-center py-12 text-sm text-content-medium">
                        {t('importConfig.loading')}
                    </div>
                ) : isRawJsonView ? (
                    <JsonEditor
                        rawJson={rawJson}
                        onRawJsonChange={handleRawJsonChange}
                        jsonError={jsonError}
                        onFormat={handleFormatJson}
                        t={t}
                    />
                ) : (
                    <div className="flex flex-col gap-10">
                        <FileNamePatternsEditor
                            patterns={fileNamePatterns}
                            onChange={handleFormChange(setFileNamePatterns)}
                            t={t}
                        />
                        <FileReaderKwargsEditor
                            kwargs={fileReaderKwargs}
                            onChange={handleFormChange(setFileReaderKwargs)}
                            t={t}
                        />
                        <ColumnsEditor
                            columns={columns}
                            onChange={handleFormChange(setColumns)}
                            schema={schema}
                            t={t}
                        />
                    </div>
                )}
            </SettingsCard>

            <SettingsCard
                title={t('importConfig.tabs.dryRun')}
                description={t('importConfig.dryRun.description', { maxRows: DRY_RUN_MAX_ROWS })}
            >
                <DryRunInputSelector
                    patterns={currentPatterns}
                    importedFileNames={fileNames}
                    mode={dryRunInputMode}
                    onModeChange={(mode) => {
                        setDryRunInputMode(mode);
                        resetDryRun();
                    }}
                    sftpPatternFiles={sftpPatternFiles}
                    uploadFileByPattern={uploadFileByPattern}
                    onUploadFileChange={(pattern, file) =>
                        setUploadFileByPattern((files) => ({ ...files, [pattern]: file }))
                    }
                    isUploadTooLarge={isUploadTooLarge}
                />

                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleDryRun}
                        disabled={!hasDryRunInput || hasJsonError}
                        loading={isDryRunRunning}
                        className="flex gap-2 items-center"
                    >
                        <DryRunIcon />
                        {t('importConfig.dryRun.runButton')}
                    </Button>
                </div>

                <DryRunPanel
                    result={dryRunResult}
                    requestError={dryRunRequestError}
                    loading={isDryRunRunning}
                    transformerConfig={formTransformerConfig}
                    schema={schema}
                    onApplyDetectedConfig={handleApplyDetectedConfig}
                    t={t}
                />
            </SettingsCard>

            <SaveBar
                hasUnsavedChanges={hasUnsavedChanges}
                onDiscard={handleDiscard}
                onSave={isRawJsonView ? handleSaveJson : handleSaveForm}
                canSave={!hasJsonError}
                isSaving={isSaving}
                t={t}
            />
        </div>
    );
};

function formatJson(value: string): string {
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
}
